import { Injectable, inject } from '@angular/core';

import type {
  AddComparisonAsIncidentClient,
  AddComparisonAsIncidentPayload,
  AddComparisonTimelinePayload,
} from '../tokens/add-comparison-as-incident.token';
import { TIMELINE_EVENTS_CLIENT } from '../tokens/timeline-events-client.token';
import type { TimelineEvent } from '../../features/variance-tracker/components/variance-supporting-data/models/timeline-event.model';

//NOTE: This is for testing purposes with the mock data, once the api is implemented this service could be removed
/** In-memory store of incident row ids added from Feature Comparison, per case. */
const addedByCaseId = new Map<string, string[]>();
/** Payload (medication, employeeName) per case and row id for display on case investigation. */
const payloadByCaseAndRow = new Map<string, Map<string, AddComparisonAsIncidentPayload>>();
/** Synthetic timeline entries per case for real-data mode fallback. */
const syntheticTimelineByCaseId = new Map<string, TimelineEvent[]>();
let nextSyntheticTimelineId = -1;

function normalizeCaseId(caseId: string): string {
  return String(caseId ?? '').trim() || 'default';
}

@Injectable({ providedIn: 'root' })
export class AddComparisonAsIncidentService implements AddComparisonAsIncidentClient {
  private readonly timelineClient = inject(TIMELINE_EVENTS_CLIENT);

  addComparisonAsIncident(caseId: string, rowId: string, payload?: AddComparisonAsIncidentPayload): void {
    const key = normalizeCaseId(caseId);
    const list = addedByCaseId.get(key) ?? [];
    if (list.includes(rowId)) return;
    list.push(rowId);
    addedByCaseId.set(key, list);

    if (payload != null && (payload.medication != null || payload.employeeName != null)) {
      let byRow = payloadByCaseAndRow.get(key);
      if (!byRow) {
        byRow = new Map();
        payloadByCaseAndRow.set(key, byRow);
      }
      byRow.set(rowId, { ...payload });
    }

    const caseIdNum = Number.parseInt(key, 10) || 0;
    this.timelineClient.addEvent?.(caseIdNum, {
      eventType: 'NewFeatureComparison',
      message: 'New Feature Comparison added as incident',
      incidentRowId: rowId,
    });
    this.addSyntheticTimelineEvent(key, {
      eventType: 'NewFeatureComparison',
      message: 'New Feature Comparison added as incident',
      incidentRowId: rowId,
    });
  }

  getAddedIncidentRowIds(caseId: string): readonly string[] {
    return addedByCaseId.get(normalizeCaseId(caseId)) ?? [];
  }

  getAddedIncidentRowPayload(caseId: string, rowId: string): AddComparisonAsIncidentPayload | undefined {
    const byRow = payloadByCaseAndRow.get(normalizeCaseId(caseId));
    return byRow?.get(rowId);
  }

  addSyntheticTimelineEvent(caseId: string, payload: AddComparisonTimelinePayload): void {
    const key = normalizeCaseId(caseId);
    const caseIdNum = Number.parseInt(key, 10) || 0;
    const id = nextSyntheticTimelineId--;
    const event: TimelineEvent = {
      id,
      message: payload.message,
      actorUserId: null,
      createdAt: new Date(),
      updatedAt: null,
      isActive: true,
      actorUser: null,
      timelineEventTarget: { id, timelineEventId: id, caseInvestigationId: caseIdNum },
      eventType: payload.eventType,
      eventSource: 'User',
      incidentRowId: payload.incidentRowId ?? null,
    };
    const current = syntheticTimelineByCaseId.get(key) ?? [];
    current.push(event);
    syntheticTimelineByCaseId.set(key, current);
  }

  getSyntheticTimelineEvents(caseId: string): readonly TimelineEvent[] {
    const key = normalizeCaseId(caseId);
    const current = syntheticTimelineByCaseId.get(key) ?? [];
    return [...current].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }
}
